<script setup>
import axios from 'axios'
import { onMounted, ref } from 'vue'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const form = ref({
  company: '',
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  stores: [{ name: '' }],
  products: [],
})

const productOptions = ref([])
const submitting = ref(false)
const errorMessage = ref('')
const loginUrl = ref('')

onMounted(async () => {
  try {
    const { data } = await axios.get('/api/public/products')

    productOptions.value = data
  } catch {
    productOptions.value = []
  }
})

const addStore = () => form.value.stores.push({ name: '' })
const removeStore = i => form.value.stores.splice(i, 1)

const submit = async () => {
  submitting.value = true
  errorMessage.value = ''
  try {
    const { data } = await axios.post('/api/public/signup', form.value)

    loginUrl.value = data.login_url
  } catch (e) {
    const validationErrors = Object.values(e.response?.data?.errors ?? {}).flat().join(' ')

    errorMessage.value = e.response?.data?.error ?? (validationErrors || 'Signup failed — please try again.')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <VContainer
    class="py-10"
    style="max-inline-size: 640px;"
  >
    <VCard
      v-if="loginUrl"
      class="pa-6 text-center"
    >
      <h2 class="mb-2">
        You're in! 🎉
      </h2>
      <p>Your trial is ready. Check your email, then sign in to get started.</p>
      <VBtn
        color="primary"
        class="mt-4"
        :href="loginUrl"
      >
        Sign in
      </VBtn>
    </VCard>

    <VCard
      v-else
      class="pa-6"
    >
      <h2 class="mb-4">
        Start your free trial
      </h2>
      <VAlert
        v-if="errorMessage"
        type="error"
        class="mb-4"
      >
        {{ errorMessage }}
      </VAlert>

      <VForm @submit.prevent="submit">
        <VTextField
          v-model="form.company"
          label="Company name"
          class="mb-3"
        />
        <VRow class="mb-0">
          <VCol cols="6">
            <VTextField
              v-model="form.first_name"
              label="First name"
            />
          </VCol>
          <VCol cols="6">
            <VTextField
              v-model="form.last_name"
              label="Last name"
            />
          </VCol>
        </VRow>
        <VTextField
          v-model="form.email"
          label="Work email"
          type="email"
          class="mb-3"
        />
        <VTextField
          v-model="form.password"
          label="Password (12+ characters)"
          type="password"
          class="mb-4"
        />

        <h4 class="mb-2">
          Your stores
        </h4>
        <div
          v-for="(store, i) in form.stores"
          :key="i"
          class="d-flex align-center mb-2"
        >
          <VTextField
            v-model="store.name"
            :label="`Store ${i + 1} name`"
            hide-details
          />
          <VBtn
            v-if="form.stores.length > 1"
            icon="tabler-x"
            variant="text"
            @click="removeStore(i)"
          />
        </div>
        <VBtn
          variant="text"
          size="small"
          class="mb-4"
          @click="addStore"
        >
          + Add another store
        </VBtn>

        <h4 class="mb-2">
          Products to try
        </h4>
        <VCheckbox
          v-for="p in productOptions"
          :key="p.key"
          v-model="form.products"
          :value="p.key"
          :label="p.name"
          hide-details
          density="compact"
        />

        <VBtn
          type="submit"
          color="primary"
          block
          class="mt-6"
          :loading="submitting"
        >
          Start free trial
        </VBtn>
      </VForm>
    </VCard>
  </VContainer>
</template>
